import React, { useEffect, useMemo, useState } from "react";
import { deleteContact, getContact, getContacts } from "../services/contactsApi";
import Layout from "../components/Layout";

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString();
  } catch {
    return "";
  }
}

export default function Contacts() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const [rows, setRows] = useState([]);
  const [pg, setPg] = useState({ page: 1, limit, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // preview drawer
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [active, setActive] = useState(null);
  const [loadingOne, setLoadingOne] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await getContacts({ page, limit, q: debouncedQ });
      setRows(data.data || []);
      setPg(data.pagination || { page, limit, total: 0, totalPages: 1 });
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedQ]);

  async function openPreview(id) {
    setOpen(true);
    setActiveId(id);
    setActive(null);
    setLoadingOne(true);

    try {
      const doc = await getContact(id);
      setActive(doc);
    } catch (e) {
      setActive({ _error: e?.response?.data?.message || "Failed to load contact" });
    } finally {
      setLoadingOne(false);
    }
  }

  function closePreview() {
    setOpen(false);
    setActiveId("");
    setActive(null);
  }

  async function onDelete(id) {
    const ok = window.confirm("Delete this contact? This cannot be undone.");
    if (!ok) return;

    try {
      await deleteContact(id);

      // if last item on page deleted, go back one page
      const remaining = rows.length - 1;
      if (remaining <= 0 && page > 1) setPage((p) => p - 1);
      else load();

      if (activeId === id) closePreview();
    } catch (e) {
      alert(e?.response?.data?.message || "Delete failed");
    }
  }

  const empty = useMemo(() => !loading && rows.length === 0, [loading, rows]);

  return (
    <Layout>
    <div className="main-card">
      <div className="topbar">
        <div>
          <div className="brand">
            <span className="brand-dot" />
            Contacts
          </div>
          <div className="small">Leads received from website form</div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div className="field" style={{ minWidth: 320 }}>
            <input
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search name, email, service..."
            />
          </div>
        </div>
      </div>

      <div style={{ padding: 18 }}>
        {err ? (
          <div className="card" style={{ padding: 14, borderRadius: "var(--radius)" }}>
            <b style={{ color: "var(--danger)" }}>Error:</b> {err}
          </div>
        ) : null}

        <div className="card" style={{ borderRadius: "var(--radius)", overflow: "hidden" }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 220 }}>Name</th>
                <th>Email</th>
                <th style={{ width: 220 }}>Service</th>
                <th style={{ width: 200 }}>Received</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="muted">
                    Loading...
                  </td>
                </tr>
              ) : null}

              {empty ? (
                <tr>
                  <td colSpan="5" className="muted">
                    No contacts found.
                  </td>
                </tr>
              ) : null}

              {!loading &&
                rows.map((r) => (
                  <tr
                    key={r._id}
                    style={{ cursor: "pointer" }}
                    onClick={() => openPreview(r._id)}
                  >
                    <td>
                      <div style={{ fontWeight: 900 }}>{r.fullName}</div>
                    </td>
                    <td>{r.email}</td>
                    <td>
                      <span className="badge">{r.service}</span>
                    </td>
                    <td>{fmtDate(r.createdAt)}</td>
                    <td>
                      <div className="actions" onClick={(e) => e.stopPropagation()}>
                        <button className="btn btn-ghost" onClick={() => openPreview(r._id)}>
                          View
                        </button>
                        <button className="btn btn-danger" onClick={() => onDelete(r._id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 14
          }}
        >
          <div className="small">
            Total: <b>{pg.total}</b>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              className="btn btn-ghost"
              disabled={pg.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>

            <span className="badge">
              Page {pg.page} / {pg.totalPages || 1}
            </span>

            <button
              className="btn btn-ghost"
              disabled={pg.page >= (pg.totalPages || 1)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Preview Drawer */}
      {open ? (
        <div style={styles.overlay} onClick={closePreview}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.drawerHead}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 16 }}>Contact Preview</div>
                <div className="small">
                  {active?.createdAt ? fmtDate(active.createdAt) : ""}
                </div>
              </div>
              <button className="btn btn-ghost" onClick={closePreview}>
                Close
              </button>
            </div>

            <div style={{ padding: 16 }}>
              {loadingOne ? (
                <div className="muted">Loading details...</div>
              ) : active?._error ? (
                <div className="card" style={{ padding: 14, borderRadius: "var(--radius)" }}>
                  <b style={{ color: "var(--danger)" }}>Error:</b> {active._error}
                </div>
              ) : active ? (
                <>
                  <div
                    className="card"
                    style={{ padding: 14, borderRadius: "var(--radius)", marginBottom: 12 }}
                  >
                    <div style={{ display: "grid", gap: 10 }}>
                      <div>
                        <span className="muted">Name:</span> <b>{active.fullName}</b>
                      </div>
                      <div>
                        <span className="muted">Email:</span> <b>{active.email}</b>
                      </div>
                      <div>
                        <span className="muted">Service:</span>{" "}
                        <span className="badge">{active.service}</span>
                      </div>
                      <div>
                        <span className="muted">Source:</span>{" "}
                        <span className="badge">{active.source || "website"}</span>
                      </div>
                      {active.page ? (
                        <div>
                          <span className="muted">Page:</span> <b>{active.page}</b>
                        </div>
                      ) : null}
                      {active.userAgent ? (
                        <div style={{ wordBreak: "break-word" }}>
                          <span className="muted">User Agent:</span> <b>{active.userAgent}</b>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="card" style={{ padding: 14, borderRadius: "var(--radius)" }}>
                    <div className="muted" style={{ fontWeight: 900, marginBottom: 8 }}>
                      Message
                    </div>
                    <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                      {active.comment}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                    <a
                      className="btn btn-primary"
                      href={`mailto:${active.email}?subject=RK Solar Energy - Reply`}
                    >
                      Reply Email
                    </a>
                    <button className="btn btn-danger" onClick={() => onDelete(active._id)}>
                      Delete
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
    </Layout>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,0.55)",
    display: "flex",
    justifyContent: "flex-end",
    zIndex: 9999
  },
  drawer: {
    width: "min(520px, 100%)",
    height: "100%",
    background: "#fff",
    borderLeft: "1px solid var(--border)",
    boxShadow: "0 20px 50px rgba(2,6,23,0.35)"
  },
  drawerHead: {
    padding: 14,
    borderBottom: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(180deg, rgba(16,31,157,0.06), transparent 70%)"
  }
};
